import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    StackProps,
    Typography
} from '@mui/material';
import UserService from '@api/Users/UserCtrl';
import { ProfileTypes, UserType } from '@api/Users/User';
import * as Yup from 'yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import PinInput from 'react-pin-input';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { showAlertError } from '@utils/Alerts/Error';
import VerifyPasswordServ from '@api/Firebase/VerifyPasswords/VerifyPasswordServ';
import { hasPermission } from '@utils/Permissions';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { BRANCH_LEAD_PROFILES } from '@utils/Permissions/Capabilities';

/** Quién tecleó el PIN (plan de inventario F2: la merma guarda quién la autorizó). */
export interface Approver {
    uid: string;
    email: string;
    name: string;
}

interface AuthorizationProps extends StackProps {
    /** Recibe a quien autorizó; los usos que no lo necesitan lo ignoran. */
    onAuthorized: (approver?: Approver) => void;
    onUnauthorized: () => void;
    isCurrentAccount?: boolean;
    /**
     * El admin también teclea su PIN (no se autoriza solo). Para lo que pide dejar
     * registro de QUIÉN autorizó con su propia mano: cancelar un gasto (vista 6).
     */
    requirePin?: boolean;
}

interface AuthorizationType {
    email: string;
    password: string;
}

const authorizationSchema: Yup.ObjectSchema<AuthorizationType> = Yup.object({
    email: Yup.string().required(),
    password: Yup.string().required('PIN requerido')
});

const toApprover = (user: UserType): Approver => ({ uid: user.uid, email: user.email, name: user.profile?.name || user.email });

const Authorization: React.FunctionComponent<AuthorizationProps> = (props) => {
    const { onAuthorized, onUnauthorized, isCurrentAccount, requirePin } = props;
    const adminBypass = hasPermission([ProfileTypes.Admin]) && !requirePin;
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const userService = useMemo(() => new UserService(), []);
    const [users, setUsers] = useState<UserType[]>([]);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<AuthorizationType>({
        resolver: yupResolver(authorizationSchema)
    });

    const getData = async () => {
        if (isCurrentAccount && loggedUser) {
            setUsers([loggedUser]);
            setValue('email', loggedUser.email);
        } else {
            // Autoriza con su PIN quien manda en una sucursal: admin, admin de sucursal y gerente.
            const approvers = await Promise.all(BRANCH_LEAD_PROFILES.map((profile) => userService.getUsersByProfile(profile)));
            setUsers(approvers.flat());
            if (hasPermission([ProfileTypes.Admin]) && loggedUser) {
                setValue('email', loggedUser.email);
            }
        }
    };

    useEffect(() => {
        void getData();
        // El admin se autoriza solo, salvo que se pida su PIN (`requirePin`).
        if (adminBypass) {
            setIsAuthorized(true);
            onAuthorized(loggedUser ? toApprover(loggedUser) : undefined);
        }
    }, []);

    const onCheckAuth: SubmitHandler<AuthorizationType> = async (values) => {
        try {
            const verified = await VerifyPasswordServ.verifyPassword(values.email, values.password);
            setIsAuthorized(verified);
            const approver = users.find((user) => user.email === values.email) || (loggedUser?.email === values.email ? loggedUser : undefined);
            verified ? onAuthorized(approver ? toApprover(approver) : undefined) : onUnauthorized();
        } catch (error) {
            showAlertError(error);
        }
    };


    useEffect(() => {
        isAuthorized ? onAuthorized() : onUnauthorized();
    }, [isAuthorized]);

    return (
        <Stack spacing={1}>
            <Typography variant="body1">{isCurrentAccount ? 'Confirma con tu PIN' : 'Autorización'}</Typography>
            <Box>
                <Grid container spacing={2}>
                    {/* Con isCurrentAccount el único usuario posible es el que ya está
                        en sesión (getData lo preselecciona), así que el select es un
                        desplegable de una sola opción: puro ruido y le roba ancho al PIN */}
                    <Grid item xs={12} display={isCurrentAccount ? 'none' : undefined}>
                        <FormControl fullWidth>
                            <InputLabel id="admin-select-label">Usuario</InputLabel>
                            <Controller
                                name="email"
                                control={control}
                                defaultValue=""
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        disabled={adminBypass}
                                        required
                                        labelId="admin-select-label"
                                        id="admin-select"
                                        value={value}
                                        label="Usuario"
                                        onChange={(e) => {
                                            onChange(e);
                                            void handleSubmit(onCheckAuth)();
                                        }}
                                        error={!!errors.email}
                                    >
                                        <MenuItem value="">Selecciona un usuario</MenuItem>
                                        {users.map((user, index) => (
                                            <MenuItem key={index} value={user.email}>
                                                {`${user.profile.name} (${user.profile.profile})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} display={adminBypass ? 'none' : undefined}>
                        {/* PIN en 6 casillas (plan de Gastos §5.4): el mismo que en el login.
                            Al completar el sexto dígito se verifica sin esperar. */}
                        <Controller
                            name="password"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange } }) => (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <PinInput
                                        length={6}
                                        secret
                                        secretDelay={100}
                                        type="numeric"
                                        inputMode="number"
                                        onChange={(value) => {
                                            onChange(value);
                                            // Borrar un dígito retira la autorización ya dada.
                                            if (value.length < 6 && isAuthorized && !adminBypass) setIsAuthorized(false);
                                        }}
                                        onComplete={() => handleSubmit(onCheckAuth)()}
                                        inputStyle={{
                                            borderColor: errors.password ? '#D32F2F' : '#B0B0B0',
                                            borderWidth: 2,
                                            borderRadius: 8,
                                            fontSize: 16,
                                            width: 36,
                                            height: 42,
                                            margin: 2
                                        }}
                                        inputFocusStyle={{ borderColor: '#177AD8' }}
                                    />
                                    {isAuthorized ? (
                                        <CheckCircleOutlineIcon color="success" />
                                    ) : (
                                        <HighlightOffIcon color="error" />
                                    )}
                                </Stack>
                            )}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Stack>
    );
};

export default Authorization;
